/**
 * The input, «Стоп», and the geolocation button — drawn as the Figma «Chat
 * Input» (07.10.2026): a frosted pill, the navy circle beside it (the design's
 * voice button; voice was removed, #53, so here it sends and stops) and the
 * design's disclaimer under both.
 *
 * **No 📍 on `peterhof_agent`** (owner, 07.10.2026): the Agent's position is
 * set in the «Отладка» drawer's «Местоположение» tab (dev only, INV-PH-65),
 * together with the museum-point search that used to sit above this input
 * (INV-PH-68). Every other graph keeps its 📍 — the canvas family's
 * navigation mode turns it into the tracking switch.
 *
 * Geolocation is asked for BY A TAP and is always the real thing — no IP
 * guesses, no cached coordinates (UI_CONTRACT §4.2). Only the permission's
 * outcome is remembered; the coordinates themselves never touch localStorage.
 *
 * **While the guide answers, nothing can be typed or sent** (owner,
 * 2026-10-18: «Запретить писать сообщение пока выполняется генерация»,
 * INV-PH-137 — it cancels the 2026-09-16 «перебивание», where a new message
 * interrupted the turn). The field is `readOnly` + `aria-disabled` (not
 * `disabled`: a disabled textarea drops focus and, on a phone, the keyboard —
 * the visitor would have to re-open it after every answer), says «Гид
 * отвечает…», and Enter neither sends nor clears the draft. The button is
 * «Стоп» for the whole turn — the one explicit way out, it always cancels the
 * turn in flight. The lock is `useSendLocked` (`chat/turnLock.ts`), the same
 * rule `App.tsx`'s `enqueueOrSend` applies as a safety net; a background
 * `peterhof_live` report run does not lock the field (a message typed over it
 * is queued, `chat/sendQueue.ts`). A message `onSend` did not take (`false`)
 * leaves the draft as it was.
 */

import { isAgentFamily } from '../store/graphFamily';
import { useEffect, useRef, useState } from 'react';
import { useChat } from '../store/chatStore';
import { useSession } from '../store/sessionStore';
import { useNavMode } from '../nav/navMode';
import { useNav } from '../nav/navStore';
import { cn } from '../lib/cn';
import { composerNote, isStrayStop, submitDraft, useSendLocked } from './turnLock';

const MAX_CHARS = 2000;

export default function Composer({
  onSend,
  onStop,
  sendDisabled = false,
}: {
  /** → `false`: the message was not taken (the draft is put back). */
  onSend: (text: string) => boolean | void;
  onStop: () => void;
  sendDisabled?: boolean;
}) {
  const status = useChat((state) => state.status);
  const draft = useChat((state) => state.draft);
  const setDraft = useChat((state) => state.setDraft);
  const error = useChat((state) => state.error);
  const geoState = useSession((state) => state.geoState);
  const setGeo = useSession((state) => state.setGeo);
  const showsGeo = useSession((state) => !isAgentFamily(state.assistantId));
  // In the navigation mode 📍 stops being a one-shot and becomes the switch for
  // continuous tracking (design §3.4). Nothing starts on its own — on iOS the
  // permission is only ever granted in answer to a gesture, so the tap IS the
  // mechanism and not merely the courtesy.
  const nav = useNavMode();
  const watching = useNav((state) => state.watching);
  const [locating, setLocating] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const area = useRef<HTMLTextAreaElement>(null);

  const busy = status !== 'idle';
  const locked = useSendLocked();
  const busySince = useRef<number | null>(null);
  useEffect(() => {
    busySince.current = busy ? Date.now() : null;
  }, [busy]);

  // 429 from nginx names how long to wait; the button says so rather than
  // failing again on the next tap (§5.3).
  useEffect(() => {
    const seconds = error?.retryAfterSeconds ?? 0;
    if (seconds <= 0) return;
    setCooldown(seconds);
    const timer = setInterval(() => {
      setCooldown((left) => {
        if (left <= 1) {
          clearInterval(timer);
          return 0;
        }
        return left - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [error]);

  useEffect(() => {
    const node = area.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, 160)}px`;
  }, [draft]);

  // While the visitor's own turn is streaming nothing is sent and the draft is
  // left alone (`locked`).
  function submit(): void {
    submitDraft({ draft, cooldown, sendDisabled, locked, onSend, setDraft });
  }

  function askForLocation(): void {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        setGeo(
          { lat: position.coords.latitude, lon: position.coords.longitude },
          'granted',
        );
      },
      () => {
        setLocating(false);
        setGeo(null, 'denied');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }

  const geoTitle = nav.active
    ? watching
      ? 'Веду по маршруту — выключить слежение'
      : geoState === 'denied'
        ? 'Доступ к геопозиции закрыт'
        : 'Вести меня по маршруту'
    : geoState === 'granted'
      ? 'Гид знает, где вы'
      : geoState === 'denied'
        ? 'Доступ к геопозиции закрыт'
        : 'Показать, что рядом со мной';

  return (
    <div className="composer">
      <div className="composer__row">
        {showsGeo ? (
          <button
            type="button"
            className={cn(
              'glass-btn',
              (nav.active ? watching : geoState === 'granted') && 'glass-btn--on',
            )}
            title={geoTitle}
            onClick={
              nav.active ? () => useNav.getState().setWatching(!watching) : askForLocation
            }
            disabled={!nav.active && locating}
            aria-pressed={nav.active ? watching : undefined}
          >
            <span className="mask-icon mask-icon--location" aria-hidden="true" />
            <span className="visually-hidden">
              {nav.active ? (watching ? 'Выключить слежение' : 'Включить слежение') : 'Я рядом'}
            </span>
          </button>
        ) : null}

        <textarea
          ref={area}
          className="composer__input"
          rows={1}
          maxLength={MAX_CHARS}
          placeholder={locked ? 'Гид отвечает…' : 'Спросите ГигаЧат'}
          aria-label="Сообщение гиду"
          readOnly={locked}
          aria-disabled={locked || undefined}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
        />

        {busy ? (
          <button
            type="button"
            className="composer__send"
            onClick={(event) => {
              if (!isStrayStop(event.detail, busySince.current, Date.now())) onStop();
            }}
            title="Остановить"
          >
            <span className="composer__stop" aria-hidden="true" />
            <span className="visually-hidden">Стоп</span>
          </button>
        ) : (
          <button
            type="button"
            className="composer__send"
            onClick={submit}
            disabled={sendDisabled || draft.trim() === '' || cooldown > 0}
            title={cooldown > 0 ? `Подождите ${cooldown} с` : 'Отправить'}
          >
            {cooldown > 0 ? cooldown : <span className="mask-icon mask-icon--send" aria-hidden="true" />}
            <span className="visually-hidden">Отправить</span>
          </button>
        )}
      </div>
      <p className="composer__note" aria-live="polite">{composerNote(locked, draft)}</p>
    </div>
  );
}
